import { forwardRef, type InputHTMLAttributes } from 'react'

import { clsx } from 'clsx'

/**
 * Checkbox Group Component
 *
 * Tile-based checkboxes with the following features:
 * - Tile design (same size as text inputs)
 * - Multiple selection support
 * - Grouping
 * - "Select all" option
 * - Required validation (at least one)
 * - Disabled options
 * - Size variants (sm, md, lg)
 *
 * Design Requirements:
 * - Checkbox tiles same height as inputs
 * - Labels align to 72px line
 * - All dimensions defined
 * - Text overflow with ellipsis
 */

export interface CheckboxOption {
  /** Option value */
  value: string
  /** Option label */
  label: string
  /** Optional description */
  description?: string
  /** Disabled state for this option */
  disabled?: boolean
}

export interface CheckboxGroupProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'value' | 'onChange'> {
  /** Selected values */
  value?: string[]
  /** On change handler */
  onChange?: (values: string[]) => void
  /** Checkbox options */
  options: CheckboxOption[]
  /** Show "Select All" option */
  showSelectAll?: boolean
  /** "Select All" label */
  selectAllLabel?: string
  /** Layout direction */
  layout?: 'horizontal' | 'vertical'
  /** Size variant (matches input sizes) */
  size?: 'sm' | 'md' | 'lg'
  /** Group name for checkboxes */
  name: string
  /** Error state */
  error?: string
  /** Custom className */
  className?: string
  /** @internal - Not for use, filtered out from DOM */
  hideMessage?: boolean
}

// =============================================================================
// Size styles
// =============================================================================

export const CHECKBOX_SIZE_STYLES = {
  sm: { height: '34px', padding: '6px 12px', fontSize: '0.875rem', lineHeight: '1.25rem', iconSize: '16px' },
  md: { height: '42px', padding: '8px 12px', fontSize: '1rem', lineHeight: '1.5rem', iconSize: '20px' },
  lg: { height: '50px', padding: '12px 16px', fontSize: '1rem', lineHeight: '1.5rem', iconSize: '24px' },
}

// =============================================================================
// Label class builder
// =============================================================================

interface LabelClassFlags {
  isSelected: boolean
  isOptionDisabled: boolean
  hasError: boolean
}

function selectedStateClasses(f: LabelClassFlags): Record<string, boolean> {
  return {
    'border-primary bg-muted': f.isSelected && !f.isOptionDisabled && !f.hasError,
    'border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-900/20': f.isSelected && !f.isOptionDisabled && f.hasError,
    'border-border bg-card': !f.isSelected && !f.isOptionDisabled && !f.hasError,
    'border-red-300 bg-card dark:border-red-600': !f.isSelected && !f.isOptionDisabled && f.hasError,
  }
}

function disabledStateClasses(f: LabelClassFlags): Record<string, boolean> {
  return {
    'border-border bg-accent': f.isOptionDisabled,
    'cursor-not-allowed': f.isOptionDisabled,
    'hover:border-primary/50': !f.isOptionDisabled && !f.hasError,
    'hover:border-red-400 dark:hover:border-red-500': !f.isOptionDisabled && f.hasError,
  }
}

export function buildCheckboxLabelClass(isSelected: boolean, isOptionDisabled: boolean, error?: string): string {
  const flags: LabelClassFlags = { isSelected, isOptionDisabled, hasError: Boolean(error) }
  return clsx(
    'flex items-center gap-3',
    'border rounded-md',
    'transition-all duration-200',
    'cursor-pointer overflow-hidden',
    { ...selectedStateClasses(flags), ...disabledStateClasses(flags) }
  )
}

// =============================================================================
// CheckboxIcon
// =============================================================================

interface CheckboxIconProps {
  isSelected: boolean
  isOptionDisabled: boolean
  iconSize: string
  error?: string
}

function CheckboxIcon({ isSelected, isOptionDisabled, iconSize, error }: CheckboxIconProps): JSX.Element {
  return (
    <div
      className={clsx(
        'flex-shrink-0 rounded border-2 flex items-center justify-center overflow-hidden',
        {
          'border-primary bg-primary': isSelected && !isOptionDisabled && !error,
          'border-red-500 bg-red-500 dark:border-red-400 dark:bg-red-400': isSelected && !isOptionDisabled && error,
          'border-border': !isSelected && !isOptionDisabled,
          'border-border bg-muted': isOptionDisabled,
        }
      )}
      style={{ width: iconSize, height: iconSize, minWidth: iconSize, minHeight: iconSize, margin: 0, padding: 0, borderWidth: '2px', borderRadius: '4px' }}
    >
      {isSelected && (
        <svg className="text-white dark:text-primary-foreground" fill="none" style={{ width: '12px', height: '12px', margin: 0, padding: 0 }} viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
      )}
    </div>
  )
}

// =============================================================================
// OptionLabel (text + description)
// =============================================================================

interface OptionLabelTextProps {
  label: string
  description?: string
  isOptionDisabled: boolean
  fontSize: string
  lineHeight: string
}

function OptionLabelText({ label, description, isOptionDisabled, fontSize, lineHeight }: OptionLabelTextProps): JSX.Element {
  return (
    <div className="flex-1 min-w-0 overflow-hidden flex flex-col" style={{ margin: 0, padding: 0, gap: '0px' }}>
      <div
        className={clsx('font-medium truncate', { 'text-foreground': !isOptionDisabled, 'text-muted-foreground': isOptionDisabled })}
        style={{ fontSize, lineHeight, margin: 0, padding: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </div>
      {description && (
        <div
          className={clsx('truncate', { 'text-muted-foreground': !isOptionDisabled, 'text-muted-foreground/50': isOptionDisabled })}
          style={{ fontSize: '0.75rem', lineHeight: '1rem', margin: 0, marginTop: '-4px', padding: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {description}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// CheckboxTile
// =============================================================================

interface CheckboxTileProps {
  option: CheckboxOption | { value: string; label: string; description?: string }
  isSelectAll?: boolean
  isSelected: boolean
  isOptionDisabled: boolean
  name: string
  currentSize: typeof CHECKBOX_SIZE_STYLES['md']
  error?: string
  inputProps: Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'value' | 'onChange' | 'checked' | 'disabled' | 'id' | 'name'>
  onSelectAll: (checked: boolean) => void
  onOptionChange: (value: string, checked: boolean) => void
}

function CheckboxTile({
  option, isSelectAll = false, isSelected, isOptionDisabled, name, currentSize, error,
  inputProps, onSelectAll, onOptionChange,
}: CheckboxTileProps): JSX.Element {
  const checkboxId = `${name}-${option.value}`

  return (
    <label
      className={buildCheckboxLabelClass(isSelected, isOptionDisabled, error)}
      htmlFor={checkboxId}
      style={{ height: currentSize.height, minHeight: currentSize.height, padding: currentSize.padding, margin: 0, borderWidth: '1px', outline: 'none' }}
    >
      <input
        checked={isSelected}
        className="sr-only"
        disabled={isOptionDisabled}
        id={checkboxId}
        name={name}
        style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}
        type="checkbox"
        value={option.value}
        onChange={(e) => isSelectAll ? onSelectAll(e.target.checked) : onOptionChange(option.value, e.target.checked)}
        {...inputProps}
      />
      <CheckboxIcon error={error} iconSize={currentSize.iconSize} isOptionDisabled={isOptionDisabled} isSelected={isSelected} />
      <OptionLabelText description={option.description} fontSize={currentSize.fontSize} isOptionDisabled={isOptionDisabled} label={option.label} lineHeight={currentSize.lineHeight} />
    </label>
  )
}

// =============================================================================
// CheckboxGroup
// =============================================================================

interface CheckboxHandlers {
  handleChange: (optionValue: string, checked: boolean) => void
  handleSelectAll: (checked: boolean) => void
}

function buildCheckboxHandlers(
  value: string[],
  options: CheckboxOption[],
  isDisabled: boolean,
  onChange?: (values: string[]) => void,
): CheckboxHandlers {
  return {
    handleChange: (optionValue, checked) => {
      if (isDisabled) { return }
      const newValues = checked ? [...value, optionValue] : value.filter((v) => v !== optionValue)
      onChange?.(newValues)
    },
    handleSelectAll: (checked) => {
      /* v8 ignore next */
      if (isDisabled) { return }
      const newValues = checked ? options.filter((opt) => !opt.disabled).map((opt) => opt.value) : []
      onChange?.(newValues)
    },
  }
}

interface BodyProps {
  forwardRef: React.Ref<HTMLDivElement>
  innerClassName?: string
  layout: 'horizontal' | 'vertical'
  currentSize: typeof CHECKBOX_SIZE_STYLES['md']
  error?: string
  inputProps: Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'value' | 'onChange'>
  isDisabled: boolean
  isAllSelected: boolean
  name: string
  options: CheckboxOption[]
  value: string[]
  showSelectAll: boolean
  selectAllLabel: string
  handleChange: (optionValue: string, checked: boolean) => void
  handleSelectAll: (checked: boolean) => void
}

function CheckboxGroupBody(p: BodyProps): JSX.Element {
  return (
    <div
      ref={p.forwardRef}
      className={clsx('w-full overflow-hidden', { 'flex flex-col gap-2': p.layout === 'vertical', 'flex flex-wrap gap-2': p.layout === 'horizontal' }, p.innerClassName)}
      role="group"
      style={{ margin: 0, padding: 0 }}
    >
      {p.showSelectAll && (
        <CheckboxTile
          isSelectAll
          currentSize={p.currentSize}
          error={p.error}
          inputProps={p.inputProps}
          isOptionDisabled={p.isDisabled}
          isSelected={p.isAllSelected}
          name={p.name}
          option={{ value: '__select_all__', label: p.selectAllLabel }}
          onOptionChange={p.handleChange}
          onSelectAll={p.handleSelectAll}
        />
      )}
      {p.options.map((option) => (
        <CheckboxTile
          key={option.value}
          currentSize={p.currentSize}
          error={p.error}
          inputProps={p.inputProps}
          isOptionDisabled={p.isDisabled || Boolean(option.disabled)}
          isSelected={p.value.includes(option.value)}
          name={p.name}
          option={option}
          onOptionChange={p.handleChange}
          onSelectAll={p.handleSelectAll}
        />
      ))}
    </div>
  )
}

export const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      value = [], onChange, options, showSelectAll = false, selectAllLabel = 'Select All',
      layout = 'vertical', size = 'md', name, error, disabled: isDisabled = false,
      className, hideMessage: _hideMessage, ...rest
    },
    ref,
  ) => {
    const currentSize = CHECKBOX_SIZE_STYLES[size]
    const { handleChange, handleSelectAll } = buildCheckboxHandlers(value, options, isDisabled, onChange)
    const isAllSelected =
      options.length > 0 &&
      options.filter((opt) => !opt.disabled).every((opt) => value.includes(opt.value))

    return (
      <CheckboxGroupBody
        currentSize={currentSize}
        error={error}
        forwardRef={ref}
        handleChange={handleChange}
        handleSelectAll={handleSelectAll}
        innerClassName={className}
        inputProps={rest}
        isAllSelected={isAllSelected}
        isDisabled={isDisabled}
        layout={layout}
        name={name}
        options={options}
        selectAllLabel={selectAllLabel}
        showSelectAll={showSelectAll}
        value={value}
      />
    )
  }
)

CheckboxGroup.displayName = 'CheckboxGroup'

export default CheckboxGroup
